import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import type { ReactNode } from 'react';
import { useSession } from '../lib/session';
import { Brand } from './Brand';
import { ThemeToggle } from './ThemeToggle';
import { NotificationBell } from './NotificationBell';
import { RunningTimerChip } from './RunningTimer';
import { SearchBox } from './SearchBox';

interface NavItem {
  to: string;
  label: string;
  icon: string;
  /**
   * Extra paths this entry owns. NavLink matches by prefix, so /companies would light up on
   * /companies/x/incidents and Incidents would not — the sidebar would point at the wrong page.
   */
  also?: (pathname: string) => boolean;
}

export function Layout({ children }: { children: ReactNode }) {
  const { session, signOut } = useSession();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  if (!session) return null;

  const isAdmin = session.person.isAdmin;
  /**
   * Administrators, and a customer's own lead.
   *
   * A client admin adds and removes their company's people, so the page they do it on has to
   * be reachable. What they see there is far narrower — their own company's clients and the
   * other client admins beside them, and nothing of ours — and the API decides that, not this.
   */
  const canSeePeople = isAdmin || session.companies.some((c) => c.capabilities['people.manage'] !== 'none');

  // Only show what the person can actually use. A client has no time to log, so putting
  // Timesheets in their sidebar would just be a link to a wall.
  const canLogTime = isAdmin || session.companies.some((c) => c.capabilities['time.log'] !== 'none');
  const canMerge = isAdmin || session.companies.some((c) => c.capabilities['change.merge'] !== 'none');
  /* Recording what went live is ours — `release.manage`, which a customer does not hold. */
  const canRelease = isAdmin || session.companies.some((c) => c.capabilities['release.manage'] !== 'none');
  /* The company overview is ours: asked by role, not by a capability a client also holds. */
  const isOurs = isAdmin || session.companies.some((c) => c.role !== null && c.role !== 'client' && c.role !== 'client_admin');
  /**
   * Incidents and Problems are hidden for now — 2026-09-11.
   *
   * Neither had been used for real: every row in the database was demo data or a test. The
   * work-item path does the same job and more (it is the one that actually carries an SLA
   * clock), and "Raise a request" was already the front door customers used. Rather than
   * delete the tables, the endpoints and their tests, the two entries are switched off here:
   * turning them back on is this one constant, which is what makes it a decision that can be
   * revisited rather than an excavation. The day Freshdesk syncs, incidents have somewhere to
   * land again.
   */
  const SERVICE_DESK_VISIBLE = false;
  const canSeeIncidents =
    SERVICE_DESK_VISIBLE &&
    (isAdmin || session.companies.some((c) => c.capabilities['incident.manage'] !== 'none'));
  const canOwnProblems =
    SERVICE_DESK_VISIBLE &&
    (isAdmin || session.companies.some((c) => c.capabilities['problem.manage'] !== 'none'));

  const delivery: NavItem[] = [
    // First, because it is the page anybody opens the Hub to look at.
    { to: '/my-work', label: 'My work', icon: '◈' },
    { to: '/companies', label: 'Companies', icon: '◲' },
    /*
      The boards, one click in rather than three.
      Everyone sees it, clients included — it shows each person the projects they already
      have, which for a customer is the work shared with their company.
    */
    { to: '/project-management', label: 'Project management', icon: '▤' },
    /* Priorities and Issues were one question asked twice; this is the one page for both. */
    { to: '/work', label: 'Work', icon: '◆' },
    ...(canLogTime ? [{ to: '/timesheets', label: 'Timesheets', icon: '◔' }] : []),
    ...(canRelease ? [{ to: '/releases', label: 'Releases', icon: '⇪' }] : []),
    ...(isOurs ? [{ to: '/overview', label: 'Company overview', icon: '◎', also: (path: string) => path.endsWith('/overview') }] : []),
    { to: '/reports', label: 'Reporting', icon: '▦' },
  ];

  /**
   * Service. "Report it" is the one entry a customer has here — the same flow as Raise a
   * request, reachable without first finding the company. What our own developers pick up
   * from it is on the Work page's Bugs lane.
   */
  const service: NavItem[] = [
    { to: '/report', label: 'Report it', icon: '⚑' },
    ...(canSeeIncidents
      ? [{ to: '/incidents', label: 'Incidents', icon: '⚠', also: (path: string) => path.endsWith('/incidents') }]
      : []),
    ...(canOwnProblems
      ? [{ to: '/problems', label: 'Problems', icon: '⌾', also: (path: string) => path.endsWith('/problems') }]
      : []),
    ...(canMerge
      ? [{ to: '/changes', label: 'Changes & merges', icon: '⑂', also: (path: string) => path.endsWith('/changes') }]
      : []),
  ];
  const admin: NavItem[] = [
    /**
     * The accounts page, now called what people actually come here to do: grant somebody a
     * role on a company. The generated capability matrix used to hold this name and is no
     * longer in the sidebar — it explained what the roles MEAN, which is a thing you read
     * once, not a page you navigate to.
     */
    ...(canSeePeople ? [{ to: '/people', label: 'Roles & access', icon: '⚿' }] : []),
    // The whole audit trail. Administrators only, and the API refuses it to anybody else.
    ...(isAdmin ? [{ to: '/audit', label: 'User activity log', icon: '◵' }] : []),
  ];
  /**
   * Nothing here now — incidents, problems and changes all shipped. Kept as an empty list
   * rather than deleted, because the section it feeds renders only when it has entries and
   * the next phase will want it back.
   */
  const planned: Array<{ label: string; icon: string }> = [];

  return (
    <div className="shell">
      <aside className="sidebar">
        <div className="row-between">
          <Brand />
          <NotificationBell />
        </div>

        {/*
          The running timer, if there is one. In the shell rather than on the timesheet, because
          a timer you started from a task and then navigated away from had nothing anywhere
          saying it was still going.
        */}
        <RunningTimerChip />

        <SearchBox />

        <nav className="stack-sm">
          <div className="nav-group-label">Delivery</div>
          {delivery.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              // `end` so a section's own page does not stay lit while you are inside it.
              end={item.to === '/companies'}
              className={({ isActive }) =>
                `nav-link ${isActive || item.also?.(pathname) ? 'active' : ''}`
              }
            >
              <span className="nav-icon" aria-hidden>
                {item.icon}
              </span>
              {item.label}
            </NavLink>
          ))}

          {service.length > 0 && (
            <>
              <div className="nav-group-label" style={{ paddingTop: 12 }}>
                Service
              </div>
              {service.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) => `nav-link ${isActive || item.also?.(pathname) ? 'active' : ''}`}
                >
                  <span className="nav-icon" aria-hidden>
                    {item.icon}
                  </span>
                  {item.label}
                </NavLink>
              ))}
            </>
          )}

          {admin.length > 0 && (
            <>
              <div className="nav-group-label" style={{ paddingTop: 12 }}>
                Administration
              </div>
              {admin.map((item) => (
                <NavLink key={item.to} to={item.to} className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}>
                  <span className="nav-icon" aria-hidden>
                    {item.icon}
                  </span>
                  {item.label}
                </NavLink>
              ))}
            </>
          )}

          {planned.length > 0 && (
            <div className="nav-group-label" style={{ paddingTop: 12 }}>
              Next phases
            </div>
          )}
          {planned.map((item) => (
            <span
              key={item.label}
              className="nav-link"
              style={{ opacity: 0.4, cursor: 'not-allowed' }}
              title="Designed, not built yet"
            >
              <span className="nav-icon" aria-hidden>
                {item.icon}
              </span>
              {item.label}
            </span>
          ))}
        </nav>

        <div className="stack-sm" style={{ marginTop: 'auto' }}>
          <ThemeToggle compact />
          <NavLink to="/settings" className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}>
            <span className="nav-icon" aria-hidden>
              ⚙
            </span>
            Settings
          </NavLink>
          <div style={{ padding: '10px', borderTop: '1px solid var(--line)' }}>
            <div className="ui-font truncate" style={{ fontSize: 13, fontWeight: 600 }}>
              {session.person.name}
            </div>
            <div className="dim truncate" style={{ fontSize: 11 }}>
              {session.person.email}
            </div>
            <button
              type="button"
              className="btn btn-quiet btn-sm"
              style={{ marginTop: 8, paddingLeft: 0 }}
              onClick={async () => {
                await signOut();
                navigate('/login', { replace: true });
              }}
            >
              Sign out
            </button>
          </div>
        </div>
      </aside>

      <main className="main">
        <div className="main-inner">{children}</div>
      </main>
    </div>
  );
}
